import { useEffect } from "react";
import { motion } from "framer-motion";
import { Coffee, Utensils, ChefHat, Truck, Store, Factory, Warehouse, ConciergeBell, ShoppingBag, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useQuote } from "./Header";
import { SITE } from "../../lib/config";
import { track } from "../../lib/analytics";

const APPLICATIONS = [
  { icon: Coffee, t: "Tea & Coffee Shops" },
  { icon: Store, t: "Bakeries & Sweet Shops" },
  { icon: ChefHat, t: "Cloud Kitchens" },
  { icon: Utensils, t: "Restaurants & QSRs" },
  { icon: ConciergeBell, t: "Caterers & Events" },
  { icon: Truck, t: "Food Delivery Brands" },
  { icon: Factory, t: "Beverage Manufacturers" },
  { icon: Warehouse, t: "Wholesale Distributors" },
  { icon: ShoppingBag, t: "Ice Cream & Dessert Brands" },
];

const SIZES = [
  {
    label: "60 ML Paper Cups",
    short: "60 ml",
    tag: "Espresso / tasting cups",
    desc: "Small paper cups for espresso, sample tasting, cutting chai and dessert samplers.",
    uses: "Espresso • Cutting chai • Tasting",
  },
  {
    label: "100 ML Paper Cups",
    short: "100 ml",
    tag: "Tea cups",
    desc: "Ideal tea-shop paper cup size for regular chai, filter coffee and small hot beverages.",
    uses: "Chai • Filter coffee",
  },
  {
    label: "150 ML Paper Cups",
    short: "150 ml",
    tag: "Coffee cups",
    desc: "Popular coffee-shop size for masala chai, cappuccino, hot chocolate and small cold drinks.",
    uses: "Coffee • Masala chai",
  },
  {
    label: "200 ML Paper Cups",
    short: "200 ml",
    tag: "Beverage cups",
    desc: "Versatile paper cup size for juice, lassi, milkshake, cold coffee and takeaway beverages.",
    uses: "Juice • Lassi • Cold coffee",
  },
  {
    label: "250 ML Paper Cups",
    short: "250 ml",
    tag: "Cold drink cups",
    desc: "Popular paper cup size for cold drinks, milkshakes, buttermilk, mocktails and party events.",
    uses: "Cold drinks • Milkshake",
  },
  {
    label: "300 ML Paper Cups",
    short: "300 ml",
    tag: "Large beverage",
    desc: "Larger paper cups for smoothies, iced coffee, cold beverages and combo takeaway packs.",
    uses: "Smoothies • Iced coffee",
  },
  {
    label: "350 ML Paper Cups",
    short: "350 ml",
    tag: "XL beverage",
    desc: "Extra-large paper cups for milkshakes, smoothies, cold coffee and party-size beverages.",
    uses: "Milkshake • Cold coffee",
  },
];

const TYPES = [
  "Single-wall paper cups",
  "Double-wall paper cups",
  "Ripple-wall paper cups",
  "Hot beverage paper cups",
  "Cold drink paper cups",
  "Custom-printed paper cups",
];

const FAQS = [
  { q: "What are paper cups used for?",
    a: "Paper cups are used by tea shops, coffee shops, cafes, cloud kitchens, restaurants, caterers and beverage brands to serve hot and cold beverages including tea, coffee, juice, milkshakes and other takeaway drinks." },
  { q: "Do you supply paper cups in wholesale quantities?",
    a: "Yes. Chennai Packs supplies disposable paper cups in wholesale/bulk quantities to tea shops, cafes, cloud kitchens, restaurants and beverage businesses. Share your required size and quantity for pricing." },
  { q: "What sizes of paper cups are available?",
    a: "Our paper cup range covers 60 ml, 100 ml, 150 ml, 200 ml, 250 ml, 300 ml and 350 ml sizes — suitable for espresso, chai, coffee, juice, milkshakes and other beverages. Enquire with your required size and quantity." },
  { q: "Do you supply paper cups with custom printing?",
    a: "Yes. Custom-printed paper cups with your logo and artwork are available at eligible MOQs. Share your logo, artwork and required quantity for a custom-print quote." },
  { q: "Are the paper cups food-grade and safe for hot drinks?",
    a: "Yes. Our paper cups are food-grade with a leak-proof PE coating suitable for hot and cold beverages." },
  { q: "Do you supply paper cups to tea shops and cafes in Chennai?",
    a: "Yes. Chennai Packs supplies paper cups to tea shops, coffee shops, cafes, cloud kitchens and caterers across Chennai, Tamil Nadu and pan-India." },
  { q: "How can I get a wholesale quote?",
    a: "Click ‘Get a Quote’ or ‘Enquire on WhatsApp’ on this page, share your required cup size and quantity, and our team will respond with wholesale pricing." },
];

export default function PaperCupsContent() {
  const openQuote = useQuote();
  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for Paper Cups.")}`;

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-paper-cups-faq", "1");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(s);
    return () => {
      document.head.querySelectorAll('script[data-paper-cups-faq="1"]').forEach((n) => n.remove());
    };
  }, []);

  return (
    <>
      <section className="py-16" data-testid="pc-intro">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Disposable Paper Cups for Tea, Coffee & Beverages
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies food-grade disposable paper cups for tea shops, coffee shops,
            cafes, cloud kitchens, restaurants, caterers and beverage brands. Our paper cup range
            is stocked in multiple sizes to serve hot beverages, cold drinks, milkshakes and
            takeaway drinks.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="pc-sizes">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Paper Cup Sizes
          </h2>
          <p className="text-[#3A4D3E] mt-3 max-w-2xl">
            Our paper cups are available in 60 ml to 350 ml — from tasting espresso cups to XL
            milkshake cups. Custom-printed cups with your logo are available at eligible MOQs.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SIZES.map((s) => {
              const waMsg = `Hi Chennai Packs, I'd like a quote for Paper Cups — ${s.short}. Please share pricing and MOQ. Thank you.`;
              const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
              return (
                <article
                  key={s.label}
                  className="bg-white border border-[#E2E8E4] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
                  data-testid={`pc-size-${s.short.replace(/\s+/g, '').toLowerCase()}`}
                >
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#1D6B2A]">
                    {s.tag}
                  </span>
                  <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-1">
                    {s.label}
                  </h3>
                  <p className="text-sm text-[#3A4D3E] mt-2 leading-relaxed">{s.desc}</p>
                  <p className="text-xs text-[#0A1C0E] mt-3 font-medium">{s.uses}</p>
                  <div className="mt-5 flex flex-col gap-2">
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("Paper Cup Size WhatsApp", { size: s.short })}
                      data-testid={`pc-wa-${s.short.replace(/\s+/g, '').toLowerCase()}`}
                      className="inline-flex items-center justify-center gap-2 border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#F8FAF8] transition-colors"
                    >
                      <MessageCircle size={14} /> WhatsApp for {s.short}
                    </a>
                    <button
                      onClick={() => openQuote({ name: `Paper Cups — ${s.short}`, sku: "CP-001" })}
                      data-testid={`pc-quote-${s.short.replace(/\s+/g, '').toLowerCase()}`}
                      className="bg-[#1D6B2A] text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#154d1e] transition-colors"
                    >
                      Get a Quote for {s.short}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="pc-applications">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Ideal for Tea, Coffee, Juice & Beverage Businesses
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPLICATIONS.map((a) => (
              <div key={a.t} className="bg-white border border-[#E2E8E4] rounded-2xl p-5 flex items-center gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                  <a.icon size={22} />
                </span>
                <span className="font-head font-semibold text-[#0A1C0E]">{a.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="pc-types">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Paper Cup Options
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <div key={t} className="bg-white border border-[#E2E8E4] rounded-xl px-5 py-4 text-[#0A1C0E]">
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="pc-wholesale">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Wholesale Paper Cups in Chennai
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies disposable paper cups to tea shops, coffee shops, cafes,
            cloud kitchens, caterers and beverage brands in Chennai. Enquire with your required
            size and quantity for wholesale pricing.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => openQuote({ name: "Paper Cups", sku: "CP-001" })}
              data-testid="pc-wholesale-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="pc-branding">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Custom Printed Paper Cups
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Print your logo and artwork on paper cups to build brand recall on every serve.
            Custom printing is available at eligible minimum order quantities. Share your logo,
            required cup size and quantity — our team will confirm feasibility and pricing.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="pc-faq">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3 mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`pc-${i}`} className="bg-white border border-[#E2E8E4] rounded-2xl px-6 data-[state=open]:shadow-md transition-shadow">
                <AccordionTrigger className="font-head text-base font-semibold text-[#0A1C0E] hover:no-underline text-left py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3A4D3E] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAF8]" data-testid="pc-related">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h3 className="font-head text-xl font-semibold text-[#0A1C0E]">Related packaging you may need</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { to: "/products/paper-straw", label: "Paper Straws" },
              { to: "/products/sipper-glasses", label: "Sipper Glasses" },
              { to: "/products/juice-pet-bottles", label: "Juice PET Bottles" },
              { to: "/products/pp-biriyani-containers", label: "PP Food Containers" },
              { to: "/products?category=paper-packaging", label: "Paper Packaging" },
            ].map((l) => (
              <a key={l.to} href={l.to} className="border border-[#E2E8E4] bg-white rounded-full px-5 py-2 text-sm font-medium text-[#1D6B2A] hover:bg-[#F0F4F0]">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="pc-final-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]"
          >
            Looking for Paper Cups in Chennai?
          </motion.h2>
          <p className="text-lg text-[#3A4D3E] mt-4 max-w-2xl mx-auto">
            Tell us your required cup size and quantity. Enquire with Chennai Packs for
            availability and wholesale pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => openQuote({ name: "Paper Cups", sku: "CP-001" })}
              data-testid="pc-final-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
